import React from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { X } from 'lucide-react';

export const CookieBanner = () => {
  const { showBanner, showMiniBadge, acceptAll, rejectNonEssential, openSettings, dismissBanner, reopenBanner } = useCookieConsent();

  /* Mini persistent badge — appears after user dismisses without choosing */
  if (showMiniBadge) {
    return (
      <button
        onClick={reopenBanner}
        className="fixed bottom-20 sm:bottom-4 left-4 z-[9998] w-10 h-10 rounded-full bg-black/80 backdrop-blur-md border border-white/10 flex items-center justify-center shadow-lg hover:bg-black hover:scale-105 transition-all duration-200"
        data-testid="cookie-mini-badge"
        aria-label="Open cookie preferences"
      >
        <svg className="w-4.5 h-4.5 text-[#C9A84C]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="8" cy="9" r="1" fill="currentColor"/><circle cx="15" cy="11" r="1" fill="currentColor"/><circle cx="10" cy="15" r="1" fill="currentColor"/><circle cx="16" cy="16" r="0.5" fill="currentColor"/></svg>
      </button>
    );
  }

  if (!showBanner) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9998] animate-slideUp"
      data-testid="cookie-banner"
    >
      <div className="bg-black/80 backdrop-blur-md border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-3.5">
          {/* Desktop: single row */}
          <div className="hidden md:flex items-center gap-6">
            <p className="text-white/55 text-[13px] leading-snug flex-1">
              We use cookies to improve your experience and analyse traffic. Manage your preferences anytime.{' '}
              <Link
                to="/cookie-policy"
                className="text-[#C9A84C] hover:text-[#D4B04A] underline underline-offset-2 transition-colors"
                data-testid="cookie-banner-learn-more"
              >
                Learn more
              </Link>
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={acceptAll}
                className="bg-[#C9A84C] hover:bg-[#D4B04A] text-black font-bold text-[10px] uppercase tracking-widest px-5 h-9 rounded-md transition-all duration-200"
                data-testid="cookie-accept-all-btn"
              >
                Accept All
              </button>
              <button
                onClick={rejectNonEssential}
                className="border border-white/20 hover:border-white/40 text-white/70 font-bold text-[10px] uppercase tracking-widest px-5 h-9 rounded-md transition-all duration-200"
                data-testid="cookie-reject-btn"
              >
                Reject
              </button>
              <button
                onClick={openSettings}
                className="text-white/35 hover:text-[#C9A84C] font-medium text-[10px] uppercase tracking-widest px-3 h-9 transition-colors duration-200"
                data-testid="cookie-manage-btn"
              >
                Settings
              </button>
              <button
                onClick={dismissBanner}
                className="w-8 h-8 flex items-center justify-center text-white/20 hover:text-white/50 transition-colors ml-1"
                data-testid="cookie-dismiss-btn"
                aria-label="Dismiss cookie banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mobile: compact stack */}
          <div className="md:hidden">
            <div className="flex items-start justify-between gap-3 mb-2.5">
              <p className="text-white/55 text-xs leading-relaxed flex-1">
                We use cookies to improve your experience.{' '}
                <Link to="/cookie-policy" className="text-[#C9A84C] underline underline-offset-2" data-testid="cookie-banner-learn-more-mobile">Learn more</Link>
              </p>
              <button
                onClick={dismissBanner}
                className="w-7 h-7 flex items-center justify-center text-white/20 hover:text-white/50 -mt-0.5 -mr-1 flex-shrink-0"
                data-testid="cookie-dismiss-btn-mobile"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={acceptAll}
                className="flex-1 bg-[#C9A84C] hover:bg-[#D4B04A] text-black font-bold text-[10px] uppercase tracking-wider h-9 rounded-md transition-all duration-200"
                data-testid="cookie-accept-all-btn-mobile"
              >
                Accept All
              </button>
              <button
                onClick={rejectNonEssential}
                className="flex-1 border border-white/20 text-white/70 font-bold text-[10px] uppercase tracking-wider h-9 rounded-md transition-all duration-200"
                data-testid="cookie-reject-btn-mobile"
              >
                Reject
              </button>
              <button
                onClick={openSettings}
                className="text-white/35 hover:text-[#C9A84C] font-bold text-[10px] uppercase tracking-wider px-3 h-9 transition-colors"
                data-testid="cookie-manage-btn-mobile"
              >
                Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
